import { Fragment, type ReactElement, useState } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { TouchBackend } from 'react-dnd-touch-backend'
import { DraggableListRow } from 'app/components/sidebar/DraggableListRow'
import { Button } from 'app/components/ui/button'
import { Separator } from 'app/components/ui/separator'
import { useIsMobile } from 'app/hooks/useMediaQuery'
import { closeModal } from 'app/lib/modal'
import type { NavListEntry, NavListsSetting } from 'app/types/navigation'
import { moveNavListKey, orderNavLists } from 'lib/navLists'

export function ManageListsDialog({
  entries,
  setting,
  onSave,
  onListsChanged,
  onAddFromTemplate,
}: {
  entries: NavListEntry[]
  setting: NavListsSetting
  onSave: (setting: NavListsSetting) => void
  onListsChanged: () => void
  onAddFromTemplate?: () => void
}): ReactElement {
  const isMobile = useIsMobile()
  const [order, setOrder] = useState(() => orderNavLists(entries, setting).map((entry) => entry.key))
  const [hidden, setHidden] = useState(() => new Set(setting.hidden))
  const byKey = new Map(entries.map((entry) => [entry.key, entry]))

  const toggleHidden = (key: string) =>
    setHidden((previous) => {
      const next = new Set(previous)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  return (
    <DndProvider
      key={isMobile ? `touch` : `html5`}
      backend={isMobile ? TouchBackend : HTML5Backend}
      options={isMobile ? { enableMouseEvents: true, delayTouchStart: 150, delayMouseStart: 150 } : undefined}
    >
      <div className="flex max-h-[70vh] w-full flex-col px-4 py-4 sm:px-6 md:w-140">
        <p className="pb-2 text-body-sm text-subtle">
          Drag to reorder. Smart lists stay on top. Hidden lists stay available here.
        </p>
        <div className="mb-3 min-h-0 grow overflow-y-auto">
          {order.map((key, index) => {
            const entry = byKey.get(key)
            if (!entry) return null
            const previous = index > 0 ? byKey.get(order[index - 1]) : undefined
            return (
              <Fragment key={key}>
                {previous?.source === `system` && entry.source !== `system` && (
                  <Separator data-built-in-divider className="mx-2 my-2 w-auto" />
                )}
                <DraggableListRow
                  entry={entry}
                  visible={!hidden.has(key)}
                  onMove={(draggedKey, targetKey) =>
                    setOrder((previous) => moveNavListKey(previous, draggedKey, targetKey))
                  }
                  onToggleVisible={() => toggleHidden(key)}
                  onListsChanged={onListsChanged}
                  // `entries` is a snapshot taken when the modal opened, so the refetch never
                  // reaches this dialog: drop the deleted row here.
                  onDeleted={() => {
                    setOrder((previous) => previous.filter((orderKey) => orderKey !== key))
                    onListsChanged()
                  }}
                />
              </Fragment>
            )
          })}
        </div>
        {/* On a phone: Add from template on its own line, Cancel and Save sharing the one
            below, full width, so none of the three is squeezed. One row from sm up. */}
        <div className="flex flex-col gap-2 border-t border-default pt-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            {onAddFromTemplate && (
              <Button variant="tertiary" size="md" className="w-full sm:w-auto" onClick={onAddFromTemplate}>
                Add from template
              </Button>
            )}
          </div>
          <div className="flex gap-2 *:flex-1 sm:*:flex-none">
            <Button variant="secondary" size="md" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={() => {
                onSave({ ...setting, order, hidden: [...hidden] })
                closeModal()
              }}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </DndProvider>
  )
}
